import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import useApi from '../hooks/useApi';
import useAuth from '../hooks/useAuth';
import Header from './Header';
import coins from '../assets/images/coins.svg';
import { useLoading } from '../context/LoadingProvider';
import localizedTitle from '../utils/localizedTitle';
import PromotionService from "../services/promotion.service";
import GeneralSettingService from '../services/general_setting.service';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../context/NotificationProvider';
import PaymentService from '../services/payment.service';
import AuthService from '../services/auth.service';
import { IconButton } from '@mui/material';
import { createEventDedupeKey, pushDataLayerEvent } from "../utils/gtmDataLayer";
import { buildEcommerceObject, buildUserDataObject, computeTaxFromTtc } from "../utils/gtmEcommerce";
import {
  extractAllowedPaymentMethods,
  resolvePaymentTypeFromSelectedCard,
} from "../utils/gtmPaymentMethods";
import GtmPaymentMethodModal from "./GtmPaymentMethodModal";
import { buildGtmEventFields } from "../utils/gtmEventFields";
import GuestCheckoutDialog from "./GuestCheckoutDialog";
import { getGiftValidityDateValue } from "../utils/giftValidity";
import { getGiftConfigurationPricing } from "../utils/giftConfigurationPricing";

const PENDING_GIFT_TRACKING_KEY = "pendingGiftTracking";

const formatChf = (value) => {
  if (value == null || value === '') {
    return value;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return value;
  }

  return Number.isInteger(numericValue) ? String(numericValue) : numericValue.toFixed(2);
};

const addYearsToDateValue = (dateValue, years) => {
  const baseDate = dateValue ? new Date(dateValue) : new Date();

  if (Number.isNaN(baseDate.getTime())) {
    return '';
  }

  const validityDate = new Date(baseDate);
  validityDate.setFullYear(validityDate.getFullYear() + years);

  return getGiftValidityDateValue(validityDate);
};

function PaymentGift() {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  const { auth } = useAuth();
  const [trackingUser, setTrackingUser] = useState(auth?.user || null);
  const { selectedGift, address, giftDetails } = location.state;
  const { lng, lat, zip_code, street, street_number, state, city, state_code, country, country_code } = address || {};
  const [surcharge, setSurcharge] = useState('');
  const { t, i18n } = useTranslation('payment_gift');
  const [isCguChecked, setIsCguChecked] = useState(false);
  const [showCguError, setShowCguError] = useState(false);
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [guestCheckoutOpen, setGuestCheckoutOpen] = useState(false);
  const [paymentMethodModalState, setPaymentMethodModalState] = useState({
    open: false,
    methods: [],
    context: null,
  });
  const cguSectionRef = useRef(null);

  const showCguValidationError = () => {
    setShowCguError(true);
    setSubmitting(false);
    notify.warn(t('cgu_required'));

    requestAnimationFrame(() => {
      cguSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      cguSectionRef.current?.querySelector('input[type="checkbox"]')?.focus({ preventScroll: true });
    });
  };

  useEffect(() => {
    setTrackingUser(auth?.user || null);
  }, [auth?.user]);

  const hasPrimaryAddress = (user) =>
    Boolean(
      user?.primary_address?.id
      || user?.primary_address?.street
      || user?.primary_address?.zip_code
      || user?.primaryAddress?.id
      || user?.primaryAddress?.street
      || user?.primaryAddress?.zip_code
    );

  const resolveTrackingUser = async () => {
    if (!auth?.user?.id) {
      return trackingUser || auth?.user || null;
    }

    const currentUser = trackingUser || auth?.user;
    if (hasPrimaryAddress(currentUser)) {
      return currentUser;
    }

    try {
      const response = await AuthService.me(
        { id: auth.user.id, with: "primary_address" },
        api
      );
      const nextUser = response?.user || currentUser;
      setTrackingUser(nextUser || null);
      return nextUser || currentUser || null;
    } catch (_) {
      return currentUser || null;
    }
  };


  useEffect(() => {
    const fetchSurcharge = async () => {
      try {
        const surcharge = await GeneralSettingService.index('setting=in:surcharge', api, auth?.accessToken ? false : true);
        setSurcharge(surcharge[0].value);
      } catch (error) {
        console.error('Error fetching surcharge data:', error);
      }
    };

    fetchSurcharge();
  }, []);


  useEffect(() => {
    if (!auth || Object.keys(auth).length === 0) {
      sessionStorage.setItem('redirectAfterLogin', '/paymentgift');
      const giftState = {
        selectedGift,
        address,
        giftDetails,
      };

      sessionStorage.setItem('state', JSON.stringify(giftState));
      // navigate('/login', { state: { from: location }, replace: true });
    }
  }, [auth, navigate, location, selectedGift, address, giftDetails]);
  const notify = useNotifications();

  const persistGiftTrackingContext = (payload = {}) => {
    try {
      const normalized = {
        promotionId: payload.promotionId ?? null,
        transactionId: payload.transactionId ?? null,
        value: payload.value,
        coupon: payload.coupon ?? "",
        paymentType: payload.paymentType ?? undefined,
        items: Array.isArray(payload.items) ? payload.items : [],
        userData:
          payload.userData && typeof payload.userData === "object" && !Array.isArray(payload.userData)
            ? payload.userData
            : {},
        savedAt: Date.now(),
      };
      sessionStorage.setItem(PENDING_GIFT_TRACKING_KEY, JSON.stringify(normalized));
    } catch (error) {
      console.error("Failed to persist gift tracking context:", error);
    }
  };

  // helper to read string or translation object
  const getLocalized = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      const lang = i18n?.language || 'en';
      return val[lang] || val.fr || val.en || '';
    }
    return String(val);
  };

  const resolvedGiftValidityDateValue = addYearsToDateValue(new Date(), 2);
  const giftValidityDisplay = i18n.language?.startsWith('en') ? '2 years' : '2 ans';
  const giftPricing = getGiftConfigurationPricing(giftDetails?.configuration);
  const shippingAmount = address?.zip_code ? Number(surcharge) || 0 : 0;
  const payableGiftTotal = giftPricing.purchasePrice + shippingAmount;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    if (!isCguChecked) {
      showCguValidationError();
      return;
    }

    const checkoutBasePrice = giftPricing.purchasePrice;
    const beginCheckoutValue = payableGiftTotal;
    const giftName = getLocalized(selectedGift?.name);
    const configTitle = localizedTitle(giftDetails?.configuration, i18n);
    const itemId = String(
      giftDetails?.configuration?.id ??
      giftDetails?.configuration?.product_id ??
      selectedGift?.id ??
      giftDetails?.configuration?.type ??
      "gift_offer"
    );
    const locationId = address?.place_id || address?.zip_code || undefined;
    const itemCategory = /hotel|h[oô]tel/i.test(giftName || "")
      ? "hotel_partenaire"
      : "a_domicile";
    const resolvedTrackingUser = await resolveTrackingUser();
    const trackingItems = [
      {
        item_id: itemId,
        item_name: configTitle || giftName || "gift_offer",
        price: checkoutBasePrice,
        quantity: 1,
        discount: 0,
        item_brand: "Flyspa",
        item_category: itemCategory,
        item_category2: configTitle || giftDetails?.configuration?.type || undefined,
        item_list_id: "massage_services",
        item_list_name: giftName || "Bons Cadeaux & Packages",
        location_id: locationId,
        index: 0,
      },
    ];
    const trackingContext = {
      value: beginCheckoutValue,
      coupon: undefined,
      items: trackingItems,
      userData: buildUserDataObject({
        user: resolvedTrackingUser,
        address,
        promotion: {
          client_id: resolvedTrackingUser?.id || auth?.user?.id,
          buyer_email: resolvedTrackingUser?.email || auth?.user?.email,
        },
        phoneNumber: resolvedTrackingUser?.phone || auth?.user?.phone,
        emailAddress: resolvedTrackingUser?.email || auth?.user?.email,
        isConnectedUser: Boolean(auth?.user?.id),
      }),
    };

    try {
      pushDataLayerEvent(
        "begin_checkout",
        {
          user_data: trackingContext.userData,
          ...buildGtmEventFields({
            language: i18n.language,
            user: resolvedTrackingUser || auth?.user,
          }),
          ecommerce: buildEcommerceObject({
            currency: "CHF",
            value: beginCheckoutValue,
            tax: computeTaxFromTtc(beginCheckoutValue),
            items: trackingItems,
          }),
        },
        {
          dedupeKey: createEventDedupeKey(
            "begin_checkout",
            "gift_flow",
            selectedGift?.id,
            itemId,
            beginCheckoutValue,
            locationId
          ),
          dedupeTtlMs: 30000,
        }
      );
    } catch (trackingError) {
      console.error("Failed to push gift begin_checkout event:", trackingError);
    }

    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';

    let promoCode = '';

    for (let i = 0; i < 3; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      promoCode += characters.charAt(randomIndex);
    }

    for (let i = 0; i < 3; i++) {
      const randomIndex = Math.floor(Math.random() * numbers.length);
      promoCode += numbers.charAt(randomIndex);
    }

    let mappedType = '';

    if (giftDetails.configuration.type === 'voucher_percentage') {
      mappedType = 'PERCENT_OFF';
    } else if (giftDetails.configuration.type === 'voucher_fixe') {
      mappedType = 'FLATT_OFF';
    } else if (giftDetails.configuration.type === 'voucher_credit') {
      mappedType = 'FREE_CREDITS';
    } else if (giftDetails.configuration.type === 'package' || giftDetails.configuration.type === 'treatment') {
      mappedType = 'FREE';
    } else if (giftDetails.configuration.type === 'packDur' || giftDetails.configuration.type === 'duration') {
      mappedType = 'DURATION';
    }

    const finalPrice = payableGiftTotal;

    const payload = {
      name: localizedTitle(giftDetails.configuration, i18n),
      code: promoCode,
      date_creation: new Date().toISOString().split('T')[0],
      date_validity: resolvedGiftValidityDateValue,
      type: mappedType,
      amount: giftPricing.promotionAmount,
      short_description: giftValidityDisplay,
      total_usage: giftDetails.configuration.usage,
      usage_per_customer: giftDetails.configuration.usage,
      is_active: 0,
      all_clients: 1,
      is_gift: 1,
      product_id: giftDetails.configuration.product_id === "null" ? null : giftDetails.configuration.product_id,
      long_description: giftDetails.message,
      from_user_name: giftDetails.from_name,
      to_user_name: giftDetails.recipient_name,
      to_user_email: giftDetails.recipient_email,
      zip_code: zip_code,
      street: street,
      city: city,
      state: state,
      state_code: state_code,
      country: country,
      country_code: country_code,
      street_number: street_number,
      longitude: lng,
      latitude: lat,
      address_id: zip_code ? auth?.user?.primary_address_id : null,
      client_id: auth?.user?.id,
      buyer_email: auth?.user?.email,
      price: finalPrice,
      clients: [],
      zones: [],
      promotionLang: i18n.language,
    };

    if (selectedCard) {
      payload.token = selectedCard.token;
    }

    try {
      const response = await PromotionService.store(payload, api);
      const transactionData = response?.data?.transaction_data || {};
      const paymentLightboxScript = transactionData?.payment_lightbox;
      const paymentUrl = transactionData?.payment_url;
      const promotionId = response?.data?.id;
      const transactionId = transactionData?.transaction_id;
      if (!paymentLightboxScript || !paymentUrl) {
        setSubmitting(false);
        return;
      }
      const allowedPaymentMethods = extractAllowedPaymentMethods(transactionData);
      const dedupeKey = createEventDedupeKey(
        "add_payment_info",
        promotionId || transactionId || "gift_payment"
      );

      persistGiftTrackingContext({
        promotionId,
        transactionId,
        value: trackingContext.value,
        coupon: trackingContext.coupon,
        userData: trackingContext.userData,
        paymentType: selectedCard
          ? resolvePaymentTypeFromSelectedCard(selectedCard)
          : undefined,
        items: trackingContext.items,
      });

      if (selectedCard) {
        pushAddPaymentInfoFromSelection({
          paymentType: resolvePaymentTypeFromSelectedCard(selectedCard),
          dedupeKey,
          ...trackingContext,
        });
        loadLightbox(paymentLightboxScript, paymentUrl);
      } else if (allowedPaymentMethods.length > 0) {
        setPaymentMethodModalState({
          open: true,
          methods: allowedPaymentMethods,
          context: {
            paymentLightboxScript,
            paymentUrl,
            dedupeKey,
            promotionId,
            transactionId,
            ...trackingContext,
          },
        });
      } else {
        loadLightbox(paymentLightboxScript, paymentUrl);
      }
    } catch (error) {
      setSubmitting(false);
    }

  };

  const loader = useLoading();

  const loadUrl = (paymentUrl) => {
    window.location.replace(paymentUrl);
  }
  const loadLightbox = (paymentLightboxScript, paymentUrl, paymentMethodId = null) => {
    loader.start();
    const script = document.createElement('script');
    script.src = paymentLightboxScript;
    script.async = true;
    script.onload = () => {
      window.LightboxCheckoutHandler.startPayment(paymentMethodId, () => { loader.stop(); loadUrl(paymentUrl) });
    };
    document.body.appendChild(script);
  }

  const pushAddPaymentInfoFromSelection = ({
    paymentType,
    value,
    coupon,
    items,
    userData,
    dedupeKey,
  }) => {
    if (!Array.isArray(items) || items.length === 0) return;

    pushDataLayerEvent(
      "add_payment_info",
      {
        user_data: userData,
        ...buildGtmEventFields({
          language: i18n.language,
          user: trackingUser || auth?.user,
        }),
        ecommerce: buildEcommerceObject({
          currency: "CHF",
          value,
          coupon,
          tax: computeTaxFromTtc(value),
          paymentType,
          items,
        }),
      },
      {
        dedupeKey,
      }
    );
  };

  const handlePaymentMethodSelection = (method) => {
    const context = paymentMethodModalState.context;
    if (!context) return;

    persistGiftTrackingContext({
      promotionId: context.promotionId,
      transactionId: context.transactionId,
      value: context.value,
      coupon: context.coupon,
      userData: context.userData,
      paymentType: method?.label,
      items: context.items,
    });

    pushAddPaymentInfoFromSelection({
      paymentType: method?.label,
      value: context.value,
      coupon: context.coupon,
      userData: context.userData,
      items: context.items,
      dedupeKey: context.dedupeKey,
    });

    setPaymentMethodModalState({ open: false, methods: [], context: null });
    loadLightbox(context.paymentLightboxScript, context.paymentUrl, method?.id ?? null);
  };

  const closePaymentMethodModal = () => {
    setPaymentMethodModalState({ open: false, methods: [], context: null });
    setSubmitting(false);
  };

  const deleteCard = async (token) => {
    try {
      await PaymentService.deleteCard(token, api);
      fetchCards();
    } catch (err) {
      console.error(err);
    }
  }

  const fetchCards = async () => {
    try {
      const response = await AuthService.me({ id: auth.user.id, with: 'cards.brand,primary_address' }, api);
      setCards(response.user.cards);
      setTrackingUser(response?.user || auth?.user || null);
      if (response?.user?.cards?.length > 0) {
        setSelectedCard(response.user.cards[0])
      } else {
        setSelectedCard(null);
      }
    } catch (err) {
      console.error(err);
    }
  }

  const handleCardChange = ($event) => {
    if ($event.currentTarget.dataset.id) {
      const card = cards.find((card) => card.id === Number($event.currentTarget.dataset.id));
      if (card) {
        setSelectedCard(card)
      }
    }
    else setSelectedCard(null);
  }

  const openGuestCheckout = () => {
    if (!auth?.user) {
      setGuestCheckoutOpen(true);
    }
  };

  const closeGuestCheckout = () => {
    setGuestCheckoutOpen(false);
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const urlsFly = {
    cgu: i18n.language === 'fr' 
      ? 'https://flyspa.ch/conditions-generales-utilisation/' 
      : 'https://flyspa.ch/en/general-terms-of-use/',
    privacy: i18n.language === 'fr' 
      ? 'https://flyspa.ch/politique-de-confidentialite/' 
      : 'https://flyspa.ch/en/privacy-policy/'
  };

  const resolveGiftSummaryImage = () => {
    const primaryImage = selectedGift?.media?.[0]?.original_url;

    if (typeof primaryImage === 'string' && primaryImage.trim().length > 0) {
      return primaryImage;
    }

    return coins;
  };

  return (
    <>
      <Header />
      <div className="gift-page">
        <div className="content-wrapper paymentgift-page">
          <div className="content-wrapper duo-pay paymentgift-page__summary">

            {giftDetails && selectedGift && (
              <div className="column">
                <div className="summary-box">
                  {selectedGift && (
                    <div className="name">
                      <div
                        className="image"
                        style={{
                          backgroundImage: `url("${resolveGiftSummaryImage()}")`,
                          backgroundPosition: 'center center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: 'contain',
                        }}
                      ></div>
                      <div className="title">
                        <div className="text-wrapper-2">
                          {getLocalized(selectedGift.name)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="div-2">
                    <div className="text-wrapper-5">{localizedTitle(giftDetails.configuration, i18n)}</div>
                  </div>

                  <div className="div-2">
                    <div className="text-wrapper-5 gift-validity-summary">
                      <span>{t('payment_gift:validity')}</span>
                      {giftValidityDisplay && (
                        <span className="gift-validity-summary__period">{giftValidityDisplay}</span>
                      )}
                    </div>
                    <div className="text-wrapper-6">{formatChf(giftPricing.purchasePrice)} CHF</div>
                  </div>
                  {giftPricing.hasDiscountedPurchase && (
                    <div className="div-2">
                      <p className="text-wrapper-5">
                        {t('payment_gift:redeem_value')}
                      </p>
                      <div className="text-wrapper-6">{formatChf(giftPricing.redemptionValue)} CHF</div>
                    </div>
                  )}
                  {address.zip_code && (

                    <div className="div-2">
                      <p className="text-wrapper-5">
                        {t('payment_gift:physical_gift_card')}
                      </p>
                      <div className="text-wrapper-6">{formatChf(surcharge)} CHF</div>
                    </div>
                  )}

                  {address.zip_code && (
                    <div className="div-2">
                      <p className="text-wrapper-5">
                        {address?.street} {address?.street_number}
                        <br />
                        {address?.zip_code} {address?.city}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="column-2 paymentgift-page__actions">
            <div className="total">
              <div className="price">
                <div className="list-item-2 list-item-3">
                  <span>{t('payment_gift:total_incl_vat')}</span>
                  <div className="text-wrapper-6">{formatChf(payableGiftTotal)} CHF</div>
                </div>
              </div>

              <div
                ref={cguSectionRef}
                className={`checkbox-2${showCguError ? ' checkbox-2--error' : ''}`}
              >
                <input
                  type="checkbox"
                  className="checkbox"
                  id="cgu"
                  checked={isCguChecked}
                  onChange={(e) => {
                    setIsCguChecked(e.target.checked);
                    if (e.target.checked) {
                      setShowCguError(false);
                      setSubmitting(false);
                    }
                  }}
                  aria-invalid={showCguError}
                />
                <label htmlFor="cgu" className="accepter-les-CGU-et">
                  <span>{t('payment_gift:accept_terms')} </span>
                  <a href={urlsFly.cgu} style={{ color: '#000', textDecoration: 'underline' }} className="text-wrapper-9" target="_blank" rel="noopener noreferrer">{t('payment_gift:terms_and_conditions')}</a>
                  <span> {t('payment_gift:est')} </span>
                  <a href={urlsFly.privacy} style={{ color: '#000', textDecoration: 'underline' }} className="text-wrapper-9" target="_blank" rel="noopener noreferrer">{t('payment_gift:privacy_policy')}</a>
                  <span> *</span>
                </label>
              </div>
            </div>
            {cards.length > 0 && (
              <div className="paymentgift-page__cards" style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '82%' }}>
                {
                  cards?.map((card, index) => (
                    <div key={index} className="payment-card flex-row align-center" style={{ gap: 20 }}>
                      <div className="flex-row gap-10 align-center" style={{ flex: 1 }}>
                        <img className='brand-icon' src={card.brand.logo} alt='card-brand' width={45} />
                        <div className='flex-column'>
                          <span className='card-holder'>{card.card_holder}</span>
                          <span className='hashed'>{card.hashed.replaceAll('X', '•')}</span>
                          <span className='expiry'>Expire {new Date(card.expiry).toLocaleDateString('en-US', { month: '2-digit', year: '2-digit' })}</span>
                        </div>
                        <IconButton onClick={() => deleteCard(card.token)}><img src='assets/images/icon-trash.svg' alt='delete' /></IconButton>
                      </div>
                      <input type='radio' name='card' style={{ cursor: 'pointer' }} checked={selectedCard === card} onChange={handleCardChange} data-id={card.id}></input>
                    </div>
                  ))
                }
                <div className="payment-card flex-row space-between align-center">
                  <span className='card-holder'>{t('newcard')}</span>
                  <input type='radio' name='card' style={{ cursor: 'pointer' }} checked={!selectedCard} onChange={handleCardChange}></input>
                </div>
              </div>
            )}
            <button
              onClick={auth?.user ? handleSubmit : openGuestCheckout}
              className="button-validation"
              type="button"
              disabled={submitting}
              style={{
                opacity: submitting ? 0.5 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              <span className="button-validation__content">
                <span className="button-validation__label">{t('pay')}</span>
                <span className="button-validation__amount">{formatChf(payableGiftTotal)} CHF{submitting ? " ..." : ""}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
      <GuestCheckoutDialog
        open={guestCheckoutOpen}
        onClose={closeGuestCheckout}
        onRegister={() => navigate('/register')}
        onLogin={() => navigate('/login', { state: { from: location.pathname } })}
        title={t('payment:guest_checkout_title')}
        message={t('payment:guest_checkout_message')}
        registerLabel={t('payment:register')}
        loginLabel={t('payment:login')}
      />
      <GtmPaymentMethodModal
        open={paymentMethodModalState.open}
        methods={paymentMethodModalState.methods}
        onSelect={handlePaymentMethodSelection}
        onCancel={closePaymentMethodModal}
      />
    </>
  );
}

export default PaymentGift;
